import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { invalidateRegistrationWizardMetadata } from '@/admin/lib/wizard-metadata-cache'
import type {
  TransactionTemplate,
  TransactionTemplateSeed,
} from '@/admin/types/transactions'
import type { PaginatedResponse } from '@/shared/types/api'
import { usePermissions } from '@/admin/hooks/use-permissions'

// ── Templates ────────────────────────────────────────────

export function useTransactionTemplates() {
  // TransactionTemplateListCreateView enforces `products.view.global` on GET -
  // note GLOBAL, not the `.acl` variant a dealer role carries.
  const { can } = usePermissions()
  return useQuery<TransactionTemplate[]>({
    queryKey: ['transaction-templates'],
    queryFn: () =>
      api
        .get<PaginatedResponse<TransactionTemplate>>('/transactions/templates/', {
          params: { page_size: 200 },
        })
        .then((r) => r.data.results),
    enabled: can('products.view.global'),
  })
}

export function useTransactionTemplate(uuid: string | null) {
  return useQuery<TransactionTemplate>({
    queryKey: ['transaction-template', uuid],
    queryFn: () =>
      api.get(`/transactions/templates/${uuid}/`).then((r) => r.data),
    enabled: !!uuid,
  })
}

export function useTransactionTemplateSeeds() {
  return useQuery<TransactionTemplateSeed[]>({
    queryKey: ['transaction-template-seeds'],
    queryFn: () =>
      api.get('/transactions/templates/seeds/').then((r) => r.data),
  })
}

export function useCreateTransactionTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      slug: string
      description?: string
      icon?: string
      seed_kind?: string
    }) =>
      api.post('/transactions/templates/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transaction-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({ queryKey: ['entity-types'] })
    },
  })
}

export function useUpdateTransactionTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      ...data
    }: {
      uuid: string
      name?: string
      slug?: string
      description?: string
      icon?: string
    }) =>
      api
        .patch(`/transactions/templates/${uuid}/`, data)
        .then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['transaction-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({
        queryKey: ['transaction-template', vars.uuid],
      })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
    },
  })
}

export function useDeleteTransactionTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/transactions/templates/${uuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transaction-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({ queryKey: ['entity-types'] })
    },
  })
}

// ── Attributes ───────────────────────────────────────────

export function useCreateTransactionAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      ...data
    }: {
      templateUuid: string
      code_name: string
      display_name: string
      field_type: string
      required?: boolean
      order?: number
      visibility?: 'PUBLIC' | 'PARTNER' | 'INTERNAL'
      configuration_data?: Record<string, unknown>
    }) =>
      api
        .post(`/transactions/templates/${templateUuid}/attributes/`, data)
        .then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['transaction-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({
        queryKey: ['transaction-template', vars.templateUuid],
      })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
    },
  })
}

export function useUpdateTransactionAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attributeUuid,
      ...data
    }: {
      templateUuid: string
      attributeUuid: string
      display_name?: string
      description?: string
      required?: boolean
      order?: number
      visibility?: 'PUBLIC' | 'PARTNER' | 'INTERNAL'
      configuration_data?: Record<string, unknown>
    }) =>
      api
        .patch(
          `/transactions/templates/${templateUuid}/attributes/${attributeUuid}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['transaction-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({
        queryKey: ['transaction-template', vars.templateUuid],
      })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
    },
  })
}

export function useDeleteTransactionAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attributeUuid,
      force,
    }: {
      templateUuid: string
      attributeUuid: string
      force?: boolean
    }) =>
      api.delete(
        `/transactions/templates/${templateUuid}/attributes/${attributeUuid}/`,
        { params: force ? { force: 'true' } : undefined },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['transaction-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({
        queryKey: ['transaction-template', vars.templateUuid],
      })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
    },
  })
}

export function useReorderTransactionAttributes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attributes,
    }: {
      templateUuid: string
      attributes: { uuid: string }[]
    }) =>
      api.patch(
        `/transactions/templates/${templateUuid}/attributes/reorder/`,
        { attributes },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['transaction-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({
        queryKey: ['transaction-template', vars.templateUuid],
      })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
    },
  })
}
